import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Project, projectService } from '../services/projectService';
import '../css/standard-components.css';

const AddProject: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id');
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    isActive: true
  });

  // Determine mode based on path
  const path = window.location.pathname;
  const isView = path.includes('/view');
  const isEdit = path.includes('/edit');

  useEffect(() => {
    if (id && (isEdit || isView)) {
      loadProject();
    }
  }, [id]);

  const loadProject = async () => {
    try {
      setLoading(true);
      const project = await projectService.getProject(parseInt(id!));
      setFormData({
        name: project.name,
        isActive: project.isActive
      });
    } catch (error) {
      console.error('Error loading project:', error);
      alert('Error loading project');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isView) return;

    try {
      setLoading(true);
      
      if (isEdit) {
        await projectService.updateProject(parseInt(id!), { name: formData.name });
        alert('Project updated successfully!');
      } else {
        await projectService.createProject({ name: formData.name });
        alert('Project created successfully!');
      }
      
      navigate('/projects');
    } catch (error) {
      console.error('Error saving project:', error);
      alert('Error saving project');
    } finally {
      setLoading(false);
    }
  };

  const getPageTitle = () => {
    if (isView) return 'View Project';
    if (isEdit) return 'Edit Project';
    return 'Add Project';
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>{getPageTitle()}</h1>
        <button 
          className="btn btn-secondary"
          onClick={() => navigate('/projects')}
        >
          Back to Projects
        </button>
      </div>

      <div className="form-container">
        <form onSubmit={handleSubmit} className="standard-form">
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="name">Project Name *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                disabled={isView}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="isActive">
                <input
                  type="checkbox"
                  id="isActive"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleInputChange}
                  disabled={isView}
                />
                Active Project
              </label>
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              onClick={() => navigate('/projects')}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            {!isView && (
              <button
                type="submit"
                disabled={loading}
                className="btn btn-primary"
              >
                {loading ? 'Saving...' : (isEdit ? 'Update Project' : 'Create Project')}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddProject;
